import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import PendingChangeWrapper from 'app/shared/componentsRestyled/common/PendingChangeWrapper';
import DOMPurify from 'dompurify';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { PendingChangesActionType } from 'constants/pendingChanges';
import { getFieldPendingChangeAuthors } from 'utils/pendingChangesAuthors';

import {
  selectIsLoanApplicationEditAllowed,
  selectIsLoanCanceled,
} from 'store/loanApplications/selectors';

import ActionButton from '../ActionButton';

import { NOTE_FIELD_LABEL, NOTE_PENDING_CHANGES_KEY } from './Note.constants';
import { noteStyles, typographyStyles } from './Note.styles';

const defaultPendingChanges = {};

const Note = ({
  isEditActive,
  note,
  pendingChanges = defaultPendingChanges,
  pendingChangeAuthors = defaultPendingChanges,
  onEditClick = _.noop,
}) => {
  const isLoanCanceled = useSelector(selectIsLoanCanceled);
  const isEditAllowed = useSelector(selectIsLoanApplicationEditAllowed);

  const isEditDisabled = !isEditAllowed || isLoanCanceled;

  const pendingChange = pendingChanges[NOTE_PENDING_CHANGES_KEY];
  const isNoteChanged = pendingChange && pendingChange.newValue !== pendingChange.initialValue;

  const sanitizedUnderwritingNotes = useMemo(() => {
    const sanitizedHtml = DOMPurify.sanitize(note, {
      WHOLE_DOCUMENT: true,
    });

    return { __html: sanitizedHtml };
  }, [note]);

  return (
    <Box sx={noteStyles}>
      <PendingChangeWrapper
        isSmall
        authors={getFieldPendingChangeAuthors(pendingChangeAuthors, NOTE_PENDING_CHANGES_KEY)}
        fieldLabel={NOTE_FIELD_LABEL}
        pendingChangesActionType={isNoteChanged ? PendingChangesActionType.MODIFIED : null}
        textSelector="span"
      >
        <Typography
          dangerouslySetInnerHTML={sanitizedUnderwritingNotes}
          sx={typographyStyles}
          variant="regular16"
        />
      </PendingChangeWrapper>

      <ActionButton
        isDisabled={isEditActive}
        isEditDisabled={isEditDisabled}
        rowData={note}
        onEdit={onEditClick}
      />
    </Box>
  );
};

Note.propTypes = {
  isEditActive: PropTypes.bool.isRequired,
  note: PropTypes.string.isRequired,
  pendingChanges: PropTypes.object,
  // Pending-change author rows by field, for the author dots on a changed note.
  pendingChangeAuthors: PropTypes.object,
  onEditClick: PropTypes.func,
};

export default Note;
